import React, { useEffect, useState } from 'react';

import { isEmpty } from 'lodash';

import { Route, Routes, useLocation, Navigate } from 'react-router-dom';

import {
  STUDENT_MAIN_ONBOARDING,
  STUDENT_FYP_ONBOARDING,
} from 'constants/userBooleans';
import withUserType from 'containers/withUserType';
import useDistrictSettings from 'hooks/useDistrictSettings';
import useRedirect from 'hooks/useRedirect';
import useUserBooleans from 'hooks/useUserBooleans';
import { UserPropTypes } from 'propTypes/UserPropTypes';
import {
  EL_STUDENT_DASHBOARD,
  DASHBOARD_STUDENT,
  ONBOARDING_STUDENT_REDIRECT,
} from 'routes/constants';
import RedirectHelper from 'routes/StudentRoutes/RedirectHelper';
import { ensureArray } from 'utils/arrays';

const StudentRedirect = ({ user }) => {
  const [isUserBooleansUpdated, setIsUserBooleansUpdated] = useState(false);
  const { isStudent, isAlumni, currentStudentGrade } = user;
  const location = useLocation();
  const { userBooleans, updateUserBooleans } = useUserBooleans();
  const { redirect } = useRedirect();

  const isElementary = currentStudentGrade <= 5;

  // Determines if student user needs to go through onboarding
  const { data: districtSettings } = useDistrictSettings({
    enabled: isStudent && currentStudentGrade > 5,
  });

  // Determines if a student needs to be taken to the student onboarding flow
  useEffect(() => {
    const setRedirectPath = async () => {
      const isStudentOnboarded = ensureArray(userBooleans).find(
        userBoolean => userBoolean?.name === STUDENT_MAIN_ONBOARDING,
      )?.value;
      const isFypCompleted = ensureArray(userBooleans).find(
        userBoolean => userBoolean?.name === STUDENT_FYP_ONBOARDING,
      )?.value;
      let redirectPath = '';
      const skipStudentOnboarding =
        isStudent && !districtSettings?.requirePathOnOnboarding;
      const isGraduatedStudent = currentStudentGrade === 99;
      const requiresOnboarding =
        isStudent &&
        !isAlumni &&
        !isStudentOnboarded &&
        !isGraduatedStudent &&
        !location.pathname.includes('/onboarding/student/path');
      if (requiresOnboarding && !skipStudentOnboarding && isFypCompleted) {
        redirectPath = ONBOARDING_STUDENT_REDIRECT;
      } else if (requiresOnboarding && !skipStudentOnboarding) {
        redirectPath = '/onboarding/student';
      } else if (
        !isUserBooleansUpdated &&
        requiresOnboarding &&
        skipStudentOnboarding
      ) {
        // Only set onboarding as completed here when the district is configured to skip onboarding.
        await updateUserBooleans({
          payload: { type: STUDENT_MAIN_ONBOARDING },
        });
        setIsUserBooleansUpdated(true);
      }
      if (redirectPath) {
        redirect({
          to: redirectPath,
          component: 'routes/Students/StudentRedirect',
          reason: `redirectPath was populated with ${redirectPath} - student needs to be onboarded`,
        });
      }
    };
    if (
      isStudent &&
      currentStudentGrade > 5 &&
      !isEmpty(userBooleans) &&
      !isEmpty(districtSettings)
    ) {
      setRedirectPath();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAlumni,
    isStudent,
    location.pathname,
    districtSettings,
    userBooleans,
    currentStudentGrade,
  ]);

  return (
    <Routes>
      <Route
        path="/dashboard"
        element={
          <Navigate
            to={isElementary ? EL_STUDENT_DASHBOARD : DASHBOARD_STUDENT}
          />
        }
      />
      {!isElementary && (
        <Route
          path="/el/student/dashboard"
          element={<Navigate to={DASHBOARD_STUDENT} />}
        />
      )}
      {isElementary && (
        <Route
          path="/dashboard/student"
          element={<Navigate to={EL_STUDENT_DASHBOARD} />}
        />
      )}
      <Route path="/me" element={<RedirectHelper isMeRedirect />} />
      <Route path="/" element={<Navigate to={DASHBOARD_STUDENT} />} />
      <Route path="/login/k12" element={<Navigate to={DASHBOARD_STUDENT} />} />
    </Routes>
  );
};

StudentRedirect.propTypes = {
  user: UserPropTypes.isRequired,
};
export default withUserType(StudentRedirect);
